import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  OutlinedInput,
  InputLabel,
  InputAdornment,
  Link,
  Tooltip,
} from "@mui/material";
import {
  SchoolOutlined,
  QueryBuilderOutlined,
  ExpandMore,
  InfoOutlined,
  FileDownloadOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";

import Spinner from "../../layout/spinner";
import ModalMessages from "../../layout/modal-messages";
import HelperMail from "../../layout/helper-mail";
import { selectUser } from "../../../store/slices/users";
import { tryDownloadCourseInformation } from "../../../store/slices/courses-availables";
import { getCourseByAccessCode } from "../../../services/apiCourses";
import { postGuestRegister } from "../../../services/apiStudents";

const GuestCourseStep = ({ currentStep, setCurrentStep, isMobile }) => {
  const user = useSelector(selectUser);
  const [accessCode, setAccessCode] = useState("");
  const [course, setCourse] = useState(null);
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const dispatch = useDispatch();

  const handleValidateCode = async () => {
    if (!accessCode) return;
    setIsLoadingCode(true);
    setErrorMsg("");
    setCourse(null);

    try {
      const response = await getCourseByAccessCode(accessCode);
      console.log("Datos del curso:", response?.data?.data || response?.data); // DEPURACIÓN
      if (response && response.data && response.data.data) {
        setCourse(response.data.data);
      } else {
        setErrorMsg("El código de acceso ingresado no es válido. Verifique la información e inténtelo nuevamente.");
      }
    } catch (error) {
      setErrorMsg("El código de acceso ingresado no es válido. Verifique la información e inténtelo nuevamente.");
    } finally {
      setIsLoadingCode(false);
    }
  };

  const handleRegister = async () => {
    setIsRegistering(true);
    setErrorMsg("");

    const userData = user.clients;
    
    // Preparar el payload según lo que el backend de guest-register espere.
    // El prompt indica: "Recibe un JSON con: Cuil, FirstName, LastName, Email, PhoneNumber, AccessCode."
    
    // Obtenemos el CUIL de los documentos
    const cuilDoc = userData.cliDocuments?.find((x) => x.typeDocumentId === 2)?.document || "";
    const phone = userData.cliAddress?.find((x) => x.typeAddress === 1)?.phone || 
                  userData.cliAddress?.find((x) => x.typeAddress === 2)?.phone || "";

    const cliAddress = (userData.cliAddress || []).map((addr) => ({
      clientId: 0,
      cliAddressId: 0,
      typeAddress: addr.typeAddress,
      fullAddress: addr.fullAddress || "",
      street: null,
      number: null,
      province: addr.province || 0,
      country: addr.country || 1,
      areaCode: addr.areaCode || "",
      phone: addr.phone || "",
    }));

    const cliSocialEconomics = userData.cliSocialEconomics
      ? {
          socialEconomicsId: 0,
          clientId: 0,
          sectionalId: null,
          situationId: userData.cliSocialEconomics.situationId ?? 3,
          academicId: userData.cliSocialEconomics.academicId ?? null,
          labouralSituationId: userData.cliSocialEconomics.labouralSituationId ?? null,
          genderId: userData.cliSocialEconomics.genderId ?? null,
          birthDate: userData.cliSocialEconomics.birthDate || null,
        }
      : null;

    const payload = {
      Cuil: cuilDoc,
      FirstName: userData.cliNaturals?.firstName || "",
      LastName: userData.cliNaturals?.lastName || "",
      Email: userData.email || "",
      PhoneNumber: phone,
      AccessCode: accessCode,
      CliAddress: cliAddress.length > 0 ? cliAddress : null,
      CliSocialEconomics: cliSocialEconomics,
    };

    try {
      const response = await postGuestRegister(payload);
      // El backend responde 200 con data: true si todo sale bien
      if (response && response.data && response.data.data === true) {
        setCurrentStep(currentStep + 1); // Avanzar a Completion
      } else {
        setErrorMsg(response?.data?.errors?.[0]?.errorMessage || "Ocurrió un error al intentar inscribirse. Intente nuevamente.");
      }
    } catch (error) {
       setErrorMsg("Ocurrió un error al intentar inscribirse. Intente nuevamente.");
    } finally {
      setIsRegistering(false);
    }
  };

  const onClickDownloadCourseInformation = async (courseId) => {
    const responseFileDownload = await dispatch(
      tryDownloadCourseInformation(courseId)
    );

    if (
      responseFileDownload.payload?.isValid &&
      responseFileDownload.payload?.data?.file
    ) {
      const base64DataUri = responseFileDownload.payload.data.file;
      const fileName =
        responseFileDownload.payload.data.name ||
        `programa_curso_${courseId}.pdf`;

      try {
        const link = document.createElement("a");
        link.href = base64DataUri;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      } catch (error) {
        console.error("Error descargando el archivo:", error);
        alert("Hubo un problema al intentar descargar el archivo.");
      }
    } else {
      const apiError =
        responseFileDownload.payload?.errors?.[0]?.errorMessage;
      alert(apiError || "No hay programa disponible para este curso.");
    }
  };

  return (
    <Grid
      container
      {...(isMobile ? { maxWidth: "xs" } : { fixed: true })}
      sx={{
        margin: isMobile ? 0 : undefined,
        padding: isMobile ? 0 : undefined,
      }}
    >
      <Grid
        sx={{
          backgroundColor: "primary.contrastText",
        }}
        item
        xs={12}
        md={12}
      >
        <Box sx={{ width: "100%" }}>
          <Grid display="flex" justifyContent="left" alignItems="left">
            <Button
              variant="text"
              disabled={isRegistering}
              sx={{
                marginTop: 2,
                marginBottom: 2,
                marginLeft: 2,
                width: 100,
              }}
              onClick={() => setCurrentStep(currentStep - 1)}
              type="button"
              style={{ fontWeight: "bold" }}
            >
              Volver
            </Button>
          </Grid>
          <Typography
            variant="h5"
            align="center"
            color="primary"
            sx={{
              paddingLeft: 5,
              paddingRight: 5,
              paddingBottom: 1,
              marginTop: isMobile ? "5px" : -5,
            }}
          >
            Sistema Integral de Formación
          </Typography>
          <Typography
            variant="h5"
            align="center"
            color="primary"
            sx={{
              fontWeight: "700",
              paddingLeft: 5,
              paddingRight: 5,
              paddingBottom: 2,
            }}
          >
            <SchoolOutlined sx={{ marginRight: 2 }} />
            Código de Invitado
          </Typography>
          <Typography
            color="primary"
            align="center"
            variant="subtitle2"
            sx={{ paddingBottom: 2 }}
          >
            Ingresá el código de acceso que te fue proporcionado para visualizar el curso.
          </Typography>

          <Container
            fixed
            sx={{
              paddingTop: 3,
            }}
          >
            <Grid
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              minHeight={160}
              gap={3}
            >
              <Box sx={{ width: isMobile ? "100%" : "500px" }}>
                <InputLabel htmlFor="accessCodeInput">Código de Acceso</InputLabel>
                <OutlinedInput
                  id="accessCodeInput"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  fullWidth
                  size="small"
                  disabled={isLoadingCode || isRegistering}
                  endAdornment={
                    <InputAdornment position="end">
                      <Button 
                        variant="contained" 
                        size="small"
                        onClick={handleValidateCode}
                        disabled={!accessCode || isLoadingCode || isRegistering}
                      >
                        {isLoadingCode ? <Spinner color="inherit" size={20} /> : "Validar"}
                      </Button>
                    </InputAdornment>
                  }
                />
              </Box>

              {errorMsg && (
                <Alert severity="error" sx={{ width: isMobile ? "100%" : "500px" }}>
                  {errorMsg}
                </Alert>
              )}

              {course && (
                <Grid
                  container
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "0px 5px 5px 5px",
                    marginBottom: "10px",
                    marginTop: "10px",
                    gap: "15px",
                    width: "310px",
                    backgroundColor: "white",
                    boxShadow: "5px 6px 5px 0px rgba(0,0,0,0.17)",
                    borderRadius: "6px",
                  }}
                >
                  <Grid item xs={12} sx={{ width: "100%" }}>
                    <Grid container spacing={1} pt={2} pb={2} direction="row" justifyContent="center">
                       <Grid item>
                         {(course.methodology || course.Methodology) && (
                            <Chip label={(course.methodology || course.Methodology).trim().toUpperCase()} />
                         )}
                       </Grid>
                    </Grid>
                    
                    <Typography variant="h6" color="#22577A" pb={1.5} align="center">
                      <strong>{(course.courseName || course.CourseName || course.name || course.briefDescription || "").trim()}</strong>
                    </Typography>

                    <Accordion
                      sx={{ border: 0, boxShadow: "none", backgroundColor: "white" }}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography pl={2}>
                          <strong><InfoOutlined /> Programa</strong>
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="subtitle2" pt={0} pb={1} mt={1} textAlign="left">
                          {course.program || course.Program || 'Sin información detallada del programa.'}
                        </Typography>
                        {(course.nameFile || course.NameFile) && (
                          <Box sx={{ mt: 1.5, textAlign: "left", pl: 2 }}>
                            <Link
                              className="hover-pointer"
                              component="button"
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (course.courseId || course.CourseId) await onClickDownloadCourseInformation(course.courseId || course.CourseId);
                              }}
                              style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: "bold" }}
                            >
                              <FileDownloadOutlined fontSize="small" /> Descargar Programa
                            </Link>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                    
                    <Divider sx={{ bgcolor: "secondary.light" }} />
                    
                    <Accordion sx={{ border: 0, boxShadow: "none", backgroundColor: "white" }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1" pl={2}>
                          <strong><QueryBuilderOutlined /> Horarios de Cursada</strong>
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ minHeight: 75 }}>
                        {(() => {
                           const groups = course.groups || course.Groups || [];
                           const hasSchedules = groups.length > 0 && groups.some(g => (g.schedules || g.Schedules) && (g.schedules || g.Schedules).length > 0);
                           return hasSchedules ? (
                             <>
                               {groups.map((g, gIdx) => {
                                  const schedules = g.schedules || g.Schedules || [];
                                  return (
                                    <React.Fragment key={gIdx}>
                                      {schedules.map((x, idx) => (
                                         <Typography key={idx} variant="subtitle2" textAlign="left" pl={1}>
                                           <span style={{ fontWeight: "bold" }}>{(x.day || x.Day) ? (x.day || x.Day).trim() : ''} </span>
                                           de {((x.startTime || x.StartTime) || '').toString().trim().substring(0, 5)} a{" "}
                                           {((x.endTime || x.EndTime) || '').toString().trim().substring(0, 5)}
                                         </Typography>
                                      ))}
                                    </React.Fragment>
                                  )
                               })}
                               <Divider sx={{ bgcolor: "secondary.light", my: 1 }} />
                             </>
                           ) : (
                             <Typography variant="subtitle2" textAlign="left" pl={1} pb={1}>
                               Sin información detallada del programa
                             </Typography>
                           );
                        })()}
                        <Typography align="left" variant="subtitle2" pl={1}>
                          <span style={{ fontWeight: "bold" }}>Fecha de Inicio:</span> {(course.initDate || course.InitDate) ? dayjs((course.initDate || course.InitDate)).format("DD/MM/YYYY") : ''}
                        </Typography>
                        <Typography align="left" variant="subtitle2" pl={1} pb={1}>
                          <span style={{ fontWeight: "bold" }}>Fecha de Fin:</span> {(course.endDate || course.EndDate) ? dayjs((course.endDate || course.EndDate)).format("DD/MM/YYYY") : ''}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                </Grid>
              )}

              {course && (
                <Box sx={{ width: isMobile ? "100%" : "500px" }}>
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={isRegistering}
                    onClick={handleRegister}
                  >
                    {isRegistering && <Spinner color="inherit" size={20} />}
                    Confirmar Inscripción
                  </Button>
                </Box>
              )}

            </Grid>
          </Container>

          <Typography
            color="primary"
            align="center"
            variant="subtitle2"
            sx={{ paddingTop: 5, paddingBottom: 2 }}
          >
            <HelperMail label="¿Tienes alguna duda? Envíanos un mail" />
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

export default GuestCourseStep;
